/**
 * AssetEditForm (UI-SPEC W9) — inline edit form replacing the identity
 * card in the side panel. Fully implemented; consumes the Phase 2 Button
 * + the new Phase 3 Select / TextArea components.
 *
 * Validates per D-65 + UI-SPEC W9 § Validation:
 *  - number: 1–32 chars, regex /^[A-Za-z0-9_-]+$/
 *  - type: one of manhole / catch_basin (Select-bound)
 *  - description: ≤ 500 chars (counter rendered)
 *  - latitude / longitude: parseable float in range, up to 6 decimals
 *
 * Save → PATCH /api/admin/assets/{id} via TanStack mutation; success
 * invalidates the assets/geojson query so the map recolors.
 */
import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Crosshair } from "lucide-react";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import { Select } from "../../components/Select";
import { TextArea } from "../../components/TextArea";
import { Banner } from "../../components/Banner";
import { usePatchAsset, type AssetFeature, type AssetType } from "../../api/assets";

interface AssetEditFormProps {
  feature: AssetFeature;
  pickedCoords: { lat: number; lng: number } | null;
  onClearPickedCoords: () => void;
  onEnterPickMode: () => void;
  onCancel: () => void;
  onSaved: () => void;
}

const NUMBER_RE = /^[A-Za-z0-9_-]{1,32}$/;
const COORD_DECIMALS_RE = /^-?\d{1,3}(?:\.\d{1,6})?$/;

function isValidLat(s: string): boolean {
  if (!COORD_DECIMALS_RE.test(s)) return false;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) && n >= -90 && n <= 90;
}
function isValidLng(s: string): boolean {
  if (!COORD_DECIMALS_RE.test(s)) return false;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) && n >= -180 && n <= 180;
}

export function AssetEditForm({
  feature,
  pickedCoords,
  onClearPickedCoords,
  onEnterPickMode,
  onCancel,
  onSaved,
}: AssetEditFormProps) {
  const intl = useIntl();
  const props = feature.properties;
  const [number, setNumber] = useState(props.number);
  const [type, setType] = useState<AssetType>(props.type);
  const [description, setDescription] = useState(props.description);
  const [latStr, setLatStr] = useState(feature.geometry.coordinates[1].toFixed(6));
  const [lngStr, setLngStr] = useState(feature.geometry.coordinates[0].toFixed(6));
  const [errors, setErrors] = useState<Record<string, string>>({});

  // If pick-on-map captured coordinates, populate the lat/lng fields.
  useEffect(() => {
    if (pickedCoords) {
      setLatStr(pickedCoords.lat.toFixed(6));
      setLngStr(pickedCoords.lng.toFixed(6));
      onClearPickedCoords();
    }
  }, [pickedCoords, onClearPickedCoords]);

  const patch = usePatchAsset();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!NUMBER_RE.test(number)) next.number = intl.formatMessage({ id: "ERR_INVALID_NUMBER_FORMAT" }, { row: 0 });
    if (!isValidLat(latStr)) next.latitude = intl.formatMessage({ id: "ERR_INVALID_COORDS" }, { row: 0 });
    if (!isValidLng(lngStr)) next.longitude = intl.formatMessage({ id: "ERR_INVALID_COORDS" }, { row: 0 });
    if (description.length > 500) next.description = intl.formatMessage({ id: "ERR_VALIDATION" });
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    patch.mutate(
      {
        id: props.id,
        payload: {
          number,
          type,
          description,
          latitude: Number.parseFloat(latStr),
          longitude: Number.parseFloat(lngStr),
        },
      },
      { onSuccess: () => onSaved() },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="asset-edit-form"
      className="pbs-4 pbe-4 ps-6 pe-6 flex flex-col gap-4"
    >
      {patch.isError && (
        <Banner variant="error" data-testid="edit-error-banner">
          <FormattedMessage id={patch.error?.code ?? "ERR_GENERIC"} />
        </Banner>
      )}

      <TextField
        id="edit-asset-number"
        label={intl.formatMessage({ id: "LABEL_ASSET_NUMBER" })}
        value={number}
        onChange={setNumber}
        error={errors.number}
        data-testid="edit-number-input"
      />

      <Select
        id="edit-asset-type"
        label={intl.formatMessage({ id: "LABEL_TYPE" })}
        value={type}
        onChange={(v) => setType(v as AssetType)}
        options={[
          { value: "manhole", label: intl.formatMessage({ id: "LABEL_TYPE_MANHOLE" }) },
          { value: "catch_basin", label: intl.formatMessage({ id: "LABEL_TYPE_CATCH_BASIN" }) },
        ]}
        data-testid="edit-type-select"
      />

      <TextArea
        id="edit-asset-description"
        label={intl.formatMessage({ id: "LABEL_DESCRIPTION" })}
        value={description}
        onChange={setDescription}
        maxLength={500}
        counter
        error={errors.description}
        data-testid="edit-description-textarea"
      />

      <div className="flex gap-3">
        <div className="flex-1">
          <TextField
            id="edit-asset-lat"
            label={intl.formatMessage({ id: "LABEL_LATITUDE" })}
            value={latStr}
            onChange={setLatStr}
            error={errors.latitude}
            data-testid="edit-lat-input"
          />
        </div>
        <div className="flex-1">
          <TextField
            id="edit-asset-lng"
            label={intl.formatMessage({ id: "LABEL_LONGITUDE" })}
            value={lngStr}
            onChange={setLngStr}
            error={errors.longitude}
            data-testid="edit-lng-input"
          />
        </div>
      </div>

      <div>
        <Button
          variant="secondary"
          type="button"
          onClick={onEnterPickMode}
          data-testid="pick-on-map-button"
        >
          <span className="inline-flex items-center gap-2">
            <Crosshair size={16} aria-hidden="true" />
            <FormattedMessage id="LABEL_PICK_ON_MAP" />
          </span>
        </Button>
      </div>

      <div className="pbs-4 border-bs border-divider flex gap-3 justify-end">
        <Button
          variant="secondary"
          type="button"
          onClick={onCancel}
          disabled={patch.isPending}
          data-testid="edit-cancel-button"
        >
          <FormattedMessage id="LABEL_CANCEL" />
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={patch.isPending}
          data-testid="edit-save-button"
        >
          <FormattedMessage id="LABEL_SAVE" />
        </Button>
      </div>
    </form>
  );
}
